import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";

/** `prompt`, in the dsh register — what the human asked, as an unlabeled
 * right-aligned bubble.
 *
 * The grammar is the native skin's and the numbers are not. dsh draws a
 * rounder, roomier bubble than chatgpt.com does — 22px of radius against
 * 18, a 525px ceiling rather than a bare percentage — and that is the whole
 * of the difference here: no label, no mark, no disclosure, and alignment
 * still owned by the component rather than by whoever appends it.
 *
 * Owning the row rather than only the bubble is the one structural thing
 * this inherits verbatim from the native skin. Right-alignment is what lets
 * a reader find every turn boundary by running an eye down the right edge,
 * and a caller who can left-align a prompt can destroy that property
 * silently.
 *
 * The ceiling is a MIN of two limits and both are load-bearing. The
 * percentage keeps the bubble from touching the agent's column on a narrow
 * pane; the pixel cap keeps a long paste from setting a 900px measure on a
 * wide one, where the line would outrun what an eye can track back to the
 * next line's start. */
@component({ name: "std-dsh-turn-prompt" })
export class DshTurnPrompt extends LonaWebComponent {
  private $text: HTMLElement = this.$("text");

  static makeWith(props: DshTurnPrompt.Props): DshTurnPrompt {
    return (DshTurnPrompt.make() as DshTurnPrompt).bind(props);
  }

  /** One field, set rather than appended, so a pooled instance carries
   * nothing of the row it used to be. */
  bind(props: DshTurnPrompt.Props): this {
    this.$text.textContent = props.text;
    return this;
  }

  static $styles = [
    css`
      /* A COLUMN, not a row: the bubble is the only child today, and a
         column with flex-end is the shape that keeps taking new children at
         the same edge. Full width for the reason the native skin states —
         in a shrink-to-fit parent the host would hug the bubble and the
         alignment would quietly do nothing. */
      :host {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        width: 100%;
        min-width: 0;
      }
      /* The tinted surface is the native skin's token, not a second one.
         Two skins disagreeing about what "the user's bubble" is made of is
         how a theme change lands in one and not the other. */
      #bubble {
        box-sizing: border-box;
        max-width: min(525px, 82%);
        padding: 10px 16px;
        border-radius: 22px;
        background: var(
          --fill-sec,
          color-mix(in srgb, var(--text-pri, #1f2328) 7%, transparent)
        );
      }
      /* The reading register. --font-size, never a literal: the ramp reads
         this knob, divides by the face cap height and trims the block's
         margins from the result, so a raw size wins the size and leaves the
         trim computed off a different number.

         --p-size, the same size as the reply it provoked, because a prompt
         is read rather than scanned. */
      #text {
        margin: 0;
        --font-size: var(--p-size);
        --line-height-ratio: 1.6;
        color: var(--text-pri, #1f2328);
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="bubble">
      <p id="text"></p>
    </div>
  `;
}

export namespace DshTurnPrompt {
  export type Props = {
    text: string;
  };
}
